/* eslint-disable no-irregular-whitespace */
import React from 'react';
import { useSelector } from 'react-redux';
import { IS_B2B_BIZ_USER } from 'reactApp/appHelpers/configHelpers';
import {
    preOverquotaBannerConfig,
    preOverquotaDebugLogger,
    preQuotaEnablePaid,
    preQuotaSizeLimit,
} from 'reactApp/appHelpers/featuresHelpers/features/overquotaPre';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { getStorage } from 'reactApp/modules/storage/storage.helpers';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { UserQuotaSelectors } from 'reactApp/modules/userQuota/userQuota.selectors';
import {
    PromoBlockBgColor,
    PromoBlockButtonColor,
    PromoBlockId,
    PromoBlockTextColor,
} from 'reactApp/ui/Mobile/PromoBlock/PromoBlock.types';
import styles from 'reactApp/ui/PreOverquotaBanner/PreOverquotaBanner.css';
import {
    isPreOverquotaBannerPeriodPassedCheck,
    usePreOverquotaBannerCallbacks,
} from 'reactApp/ui/PreOverquotaBanner/PreOverquotaBanner.helpers';
import { type ConditionsWithData, checkConditions } from 'reactApp/utils/conditionChecker';
import { ON_PREMISE } from 'server/constants/environment';

export const usePreOverquoterBlock = (setShowData: (value: null) => void) => {
    const isOverquota = useSelector(UserQuotaSelectors.isOverquota);
    const isPaidUser = useSelector(UserSelectors.isPaidUser);
    const isCorp = useSelector(UserSelectors.isCorpUser);
    const { used: usedPercent } = useSelector(UserQuotaSelectors.getFilledPercents);
    const { used, userTotalSpace } = useSelector(UserQuotaSelectors.getUserQuotaState);
    const storage = useSelector(getCurrentStorage);
    const { isAllDocuments, isFamilyPage, isFamilyAlbums, isFamilyFolders } = getStorage(storage);

    const showPreOverquotaBannerConditions: ConditionsWithData = {
        preOverquotaBanner: preOverquotaBannerConfig.enable,
        isNotBiz: !IS_B2B_BIZ_USER,
        isNotOnPrem: !ON_PREMISE,
        isNotCorp: !isCorp,
        isNotPaidCheck: { check: preQuotaEnablePaid || !isPaidUser, preQuotaEnablePaid, isPaidUser },
        isNotDocs: !isAllDocuments,
        isNotFamilyPage: !isFamilyPage,
        isNotFamilyAlbums: !isFamilyAlbums,
        isNotFamilyFolders: !isFamilyFolders,
        isNotOverquota: !isOverquota,

        overLimitCheck: { check: usedPercent >= preQuotaSizeLimit, usedPercent, preQuotaOfferLimit: preQuotaSizeLimit },
        isPeriodPassed: isPreOverquotaBannerPeriodPassedCheck,
    };

    const { result: showPreOverquotaBanner, resolvedConditions: preOverquotaBannerResolvedConditions } =
        checkConditions(showPreOverquotaBannerConditions);

    preOverquotaDebugLogger.info('preOverquotaMobileBanner', preOverquotaBannerResolvedConditions);

    const { onShowHandler, onPrimaryHandler, onSecondaryHandler, onCancelHandler } = usePreOverquotaBannerCallbacks({
        hide: () => {
            setShowData(null);
        },
    });

    const preOverquotaPromoData = {
        onShow: onShowHandler,
        onClose: onCancelHandler,
        onClick: onPrimaryHandler,
        onSecondaryClick: onSecondaryHandler,
        doNotClose: true,
    };

    return {
        showPreOverquotaBanner,
        preOverquotaPromoProps: {
            id: PromoBlockId.PRE_OVERQUOTA,
            title: (
                <>
                    Смотреть и загружать файлы
                    <br />
                    скоро будет нельзя
                </>
            ),
            textColor: PromoBlockTextColor.black,
            buttonColor: PromoBlockButtonColor.red,
            buttonSecondaryColor: PromoBlockButtonColor.white,
            backgroundColor: PromoBlockBgColor.lightRed,
            image: '',
            imageFullHeight: false,
            showOnce: false,
            closable: preOverquotaBannerConfig.closable,
            buttonText: preOverquotaBannerConfig.primaryButton?.enable ? preOverquotaBannerConfig.primaryButton?.name : '',
            buttonSecondaryText: preOverquotaBannerConfig.secondButton?.enable ? preOverquotaBannerConfig.secondButton?.name : '',
            text: (
                <div className={styles.content}>
                    В едином пространстве Почты и Облака <span>{used?.space}</span> из <span>{userTotalSpace?.value}</span> — оно почти
                    переполнено. Увеличьте его с подпиской Mail Space
                </div>
            ),
        },
        preOverquotaPromoData,
    };
};
